// Translation Service for FarmTrackPro
// Provides Romanian/English language support
import { useState, useEffect } from 'react';

class TranslationService {
  constructor() {
    this.currentLanguage = this.getStoredLanguage() || 'ro'; // Default to Romanian
    this.listeners = new Set();
  }

  // Get stored language preference
  getStoredLanguage() {
    return localStorage.getItem('farmtrackpro_language');
  }

  // Set language and persist preference
  setLanguage(language) {
    this.currentLanguage = language;
    localStorage.setItem('farmtrackpro_language', language);
    this.notifyListeners();
  }

  // Get current language
  getCurrentLanguage() {
    return this.currentLanguage;
  }

  // Add language change listener
  addLanguageListener(callback) {
    this.listeners.add(callback);
    return () => this.listeners.delete(callback);
  }

  // Notify all listeners of language change
  notifyListeners() {
    this.listeners.forEach(callback => callback(this.currentLanguage));
  }

  // Translation dictionary
  translations = {
    // Navigation & General
    dashboard: { ro: 'Tablou de Bord', en: 'Dashboard' },
    fields: { ro: 'Câmpuri', en: 'Fields' },
    weather: { ro: 'Vreme', en: 'Weather' },
    tasks: { ro: 'Sarcini', en: 'Tasks' },
    inventory: { ro: 'Inventar', en: 'Inventory' },
    finances: { ro: 'Finanțe', en: 'Finances' },
    settings: { ro: 'Setări', en: 'Settings' },
    'user-management': { ro: 'Gestionare Utilizatori', en: 'User Management' },
    'parcel-manager': { ro: 'Manager Parcele', en: 'Parcel Manager' },
    'work-session': { ro: 'Sesiune Lucru', en: 'Work Session' },
    'nav-equipment': { ro: 'Manager Echipamente', en: 'Equipment Manager' },
    'nav-seasons': { ro: 'Management Sezoane', en: 'Season Management' },
    'nav-stockadjustment': { ro: 'Ajustare Stoc', en: 'Stock Adjustment' },
    'nav-workplanning': { ro: 'Planificare Lucrări', en: 'Work Planning' },
    'nav-arendamanager': { ro: 'Manager Arendă', en: 'Arenda Manager' },

    // Common actions
    save: { ro: 'Salvează', en: 'Save' },
    cancel: { ro: 'Anulează', en: 'Cancel' },
    delete: { ro: 'Șterge', en: 'Delete' },
    edit: { ro: 'Editează', en: 'Edit' },
    add: { ro: 'Adaugă', en: 'Add' },
    view: { ro: 'Vezi', en: 'View' },
    search: { ro: 'Caută', en: 'Search' },
    filter: { ro: 'Filtrează', en: 'Filter' },
    export: { ro: 'Exportă', en: 'Export' },
    import: { ro: 'Importă', en: 'Import' },
    refresh: { ro: 'Actualizează', en: 'Refresh' },
    loading: { ro: 'Se încarcă...', en: 'Loading...' },
    error: { ro: 'Eroare', en: 'Error' },
    success: { ro: 'Succes', en: 'Success' },
    warning: { ro: 'Avertisment', en: 'Warning' },
    info: { ro: 'Informație', en: 'Info' },

    // Authentication
    login: { ro: 'Autentificare', en: 'Login' },
    logout: { ro: 'Deconectare', en: 'Logout' },
    register: { ro: 'Înregistrare', en: 'Register' },
    email: { ro: 'Email', en: 'Email' },
    password: { ro: 'Parolă', en: 'Password' },
    'confirm-password': { ro: 'Confirmă Parola', en: 'Confirm Password' },
    'forgot-password': { ro: 'Ai uitat parola?', en: 'Forgot Password?' },
    'farm-name': { ro: 'Numele Fermei', en: 'Farm Name' },
    'invitation-code': { ro: 'Cod de Invitație', en: 'Invitation Code' },
    'full-name': { ro: 'Nume Complet', en: 'Full Name' },

    // Parcel Manager
    'add-parcel': { ro: 'Adaugă Parcelă', en: 'Add Parcel' },
    'parcel-name': { ro: 'Numele Parcelei', en: 'Parcel Name' },
    'parcel-area': { ro: 'Suprafața (ha)', en: 'Area (ha)' },
    'contract-date': { ro: 'Data Contract', en: 'Contract Date' },
    'expiry-date': { ro: 'Data Expirare', en: 'Expiry Date' },
    'farmer-name': { ro: 'Nume Fermier', en: 'Farmer Name' },
    cnp: { ro: 'CNP', en: 'CNP' },
    firma: { ro: 'Firma', en: 'Company' },
    location: { ro: 'Locație', en: 'Location' },
    parcels: { ro: 'Parcele', en: 'Parcels' },
    'total-area': { ro: 'Suprafață Totală', en: 'Total Area' },
    'total-parcels': { ro: 'Total Parcele', en: 'Total Parcels' },

    // Weather
    'weather-forecast': { ro: 'Prognoza Meteo', en: 'Weather Forecast' },
    'current-weather': { ro: 'Vremea Curentă', en: 'Current Weather' },
    temperature: { ro: 'Temperatură', en: 'Temperature' },
    humidity: { ro: 'Umiditate', en: 'Humidity' },
    wind: { ro: 'Vânt', en: 'Wind' },
    pressure: { ro: 'Presiune', en: 'Pressure' },
    'feels-like': { ro: 'Se simte ca', en: 'Feels like' },
    'weather-alerts': { ro: 'Alerte Meteo', en: 'Weather Alerts' },
    precipitation: { ro: 'Precipitații', en: 'Precipitation' },
    'last-updated': { ro: 'Ultima actualizare', en: 'Last updated' },

    // Weather conditions
    'clear': { ro: 'Senin', en: 'Clear' },
    'sunny': { ro: 'Însorit', en: 'Sunny' },
    'partly-cloudy': { ro: 'Parțial Noros', en: 'Partly Cloudy' },
    'cloudy': { ro: 'Noros', en: 'Cloudy' },
    'rain': { ro: 'Ploaie', en: 'Rain' },
    'thunderstorms': { ro: 'Furtuni', en: 'Thunderstorms' },
    'snow': { ro: 'Ninsoare', en: 'Snow' },
    'fog': { ro: 'Ceață', en: 'Fog' },

    // Inventory
    materials: { ro: 'Materiale', en: 'Materials' },
    equipment: { ro: 'Echipamente', en: 'Equipment' },
    invoices: { ro: 'Facturi', en: 'Invoices' },
    'add-material': { ro: 'Adaugă Material', en: 'Add Material' },
    'add-equipment': { ro: 'Adaugă Echipament', en: 'Add Equipment' },
    'material-name': { ro: 'Nume Material', en: 'Material Name' },
    'material-type': { ro: 'Tip Material', en: 'Material Type' },
    quantity: { ro: 'Cantitate', en: 'Quantity' },
    unit: { ro: 'Unitate', en: 'Unit' },
    supplier: { ro: 'Furnizor', en: 'Supplier' },
    'purchase-date': { ro: 'Data Achiziție', en: 'Purchase Date' },
    'equipment-name': { ro: 'Nume Echipament', en: 'Equipment Name' },
    'equipment-type': { ro: 'Tip Echipament', en: 'Equipment Type' },
    model: { ro: 'Model', en: 'Model' },
    status: { ro: 'Status', en: 'Status' },
    'last-maintenance': { ro: 'Ultima Întreținere', en: 'Last Maintenance' },
    'next-maintenance': { ro: 'Următoarea Întreținere', en: 'Next Maintenance' },

    // Equipment types
    herbicide: { ro: 'Erbicid', en: 'Herbicide' },
    fertilizer: { ro: 'Îngrășământ', en: 'Fertilizer' },
    seeds: { ro: 'Semințe', en: 'Seeds' },
    tractor: { ro: 'Tractor', en: 'Tractor' },
    sprayer: { ro: 'Pulverizator', en: 'Sprayer' },
    plow: { ro: 'Plug', en: 'Plow' },
    harvester: { ro: 'Combină', en: 'Harvester' },
    trailer: { ro: 'Remorcă', en: 'Trailer' },

    // Status
    operational: { ro: 'Operațional', en: 'Operational' },
    maintenance: { ro: 'În Întreținere', en: 'Maintenance' },
    'out-of-order': { ro: 'Defect', en: 'Out of Order' },

    // User Management
    'create-user': { ro: 'Creează Utilizator', en: 'Create User' },
    'user-role': { ro: 'Rol Utilizator', en: 'User Role' },
    manager: { ro: 'Manager', en: 'Manager' },
    worker: { ro: 'Muncitor', en: 'Worker' },
    accountant: { ro: 'Contabil', en: 'Accountant' },
    'reset-password': { ro: 'Resetează Parola', en: 'Reset Password' },
    'deactivate-user': { ro: 'Dezactivează Utilizator', en: 'Deactivate User' },
    'activate-user': { ro: 'Activează Utilizator', en: 'Activate User' },

    // Tasks
    'task-title': { ro: 'Titlu Sarcină', en: 'Task Title' },
    'task-description': { ro: 'Descriere Sarcină', en: 'Task Description' },
    'assigned-to': { ro: 'Atribuit lui', en: 'Assigned to' },
    'assignedTo': { ro: 'Atribuit către', en: 'Assigned To' },
    'due-date': { ro: 'Termen Limită', en: 'Due Date' },
    priority: { ro: 'Prioritate', en: 'Priority' },
    'task-status': { ro: 'Status Sarcină', en: 'Task Status' },
    high: { ro: 'Înaltă', en: 'High' },
    medium: { ro: 'Medie', en: 'Medium' },
    low: { ro: 'Scăzută', en: 'Low' },
    pending: { ro: 'În Așteptare', en: 'Pending' },
    'in-progress': { ro: 'În Progres', en: 'In Progress' },
    completed: { ro: 'Completat', en: 'Completed' },
    scheduled: { ro: 'Programat', en: 'Scheduled' },
    'loadingWorkers': { ro: 'Se încarcă lucrătorii', en: 'Loading workers' },
    'selectWorker': { ro: 'Selectează Lucrătorul', en: 'Select Worker' },
    'noWorkersAvailable': { ro: 'Nu sunt lucrători disponibili', en: 'No workers available' },
    'addWorkersInUserManagement': { ro: 'Adaugă lucrători în Managementul Utilizatorilor', en: 'Add workers in User Management' },

    // Fields
    'field-name': { ro: 'Nume Câmp', en: 'Field Name' },
    'field-area': { ro: 'Suprafață Câmp', en: 'Field Area' },
    crop: { ro: 'Cultură', en: 'Crop' },
    'crop-stage': { ro: 'Stadiul Culturii', en: 'Crop Stage' },
    'field-health': { ro: 'Sănătatea Câmpului', en: 'Field Health' },
    contract: { ro: 'Contract', en: 'Contract' },

    // Settings
    'language-settings': { ro: 'Setări Limbă', en: 'Language Settings' },
    'select-language': { ro: 'Selectează Limba', en: 'Select Language' },
    romanian: { ro: 'Română', en: 'Romanian' },
    english: { ro: 'Engleză', en: 'English' },
    'farm-settings': { ro: 'Setări Fermă', en: 'Farm Settings' },
    'weather-locations': { ro: 'Locații Meteo', en: 'Weather Locations' },
    'company-settings': { ro: 'Setări Companie', en: 'Company Settings' },
    'location-settings': { ro: 'Setări Locație', en: 'Location Settings' },

    // Season Management
    'seasonManagement': { ro: 'Managementul Sezonului', en: 'Season Management' },
    'seasonManagementDescription': { ro: 'Gestionează sezoanele agricole, închide anul și compară performanțele', en: 'Manage agricultural seasons, close the year and compare performances' },
    'currentSeason': { ro: 'Sezonul Curent', en: 'Current Season' },
    'seasonEndingWarning': { ro: 'Sezonul se apropie de sfârșit', en: 'Season is ending soon' },
    'closeSeasonTitle': { ro: 'Închide Sezonul', en: 'Close Season' },
    'closeSeasonDescription': { ro: 'Închide sezonul curent și începe unul nou pentru următorul an agricol', en: 'Close current season and start a new one for the next agricultural year' },
    'closeSeason': { ro: 'Închide Sezonul', en: 'Close Season' },
    'confirmCloseSeason': { ro: 'Confirmă Închiderea Sezonului', en: 'Confirm Season Closure' },
    'closeSeasonConfirmation': { ro: 'Ești sigur că vrei să închizi sezonul curent? Această acțiune nu poate fi anulată.', en: 'Are you sure you want to close the current season? This action cannot be undone.' },
    'closingNotes': { ro: 'Note de Închidere', en: 'Closing Notes' },
    'addClosingNotes': { ro: 'Adaugă note despre sezonul care se închide...', en: 'Add notes about the closing season...' },
    'closing': { ro: 'Se închide...', en: 'Closing...' },
    'overview': { ro: 'Prezentare Generală', en: 'Overview' },
    'rotation': { ro: 'Rotația Culturilor', en: 'Crop Rotation' },
    'comparison': { ro: 'Comparație', en: 'Comparison' },
    'history': { ro: 'Istoric', en: 'History' },
    'seasonDates': { ro: 'Datele Sezonului', en: 'Season Dates' },
    'startDate': { ro: 'Data de Început', en: 'Start Date' },
    'endDate': { ro: 'Data de Sfârșit', en: 'End Date' },
    'activeContracts': { ro: 'Contracte Active', en: 'Active Contracts' },
    'closedOn': { ro: 'Închis pe', en: 'Closed on' },
    'noRotationData': { ro: 'Nu există date pentru rotația culturilor din sezoanele anterioare', en: 'No crop rotation data from previous seasons' },
    'cropRotationRecommendations': { ro: 'Recomandări pentru Rotația Culturilor', en: 'Crop Rotation Recommendations' },
    'previousCrop': { ro: 'Cultura Anterioară', en: 'Previous Crop' },
    'recommendedCrops': { ro: 'Culturi Recomandate', en: 'Recommended Crops' },
    'reason': { ro: 'Motiv', en: 'Reason' },
    'benefits': { ro: 'Beneficii', en: 'Benefits' },
    'seasonComparison': { ro: 'Comparația Sezoanelor', en: 'Season Comparison' },
    'selectSeason': { ro: 'Selectează Sezonul', en: 'Select Season' },
    'compare': { ro: 'Compară', en: 'Compare' },
    'comparisonResults': { ro: 'Rezultatele Comparației', en: 'Comparison Results' },
    'season': { ro: 'Sezonul', en: 'Season' },
    'seasonHistory': { ro: 'Istoricul Sezoanelor', en: 'Season History' },
    'area': { ro: 'Suprafață', en: 'Area' },
    'notes': { ro: 'Note', en: 'Notes' },
    'active': { ro: 'Activ', en: 'Active' },
    'closed': { ro: 'Închis', en: 'Closed' },
    'pleaseLogin': { ro: 'Vă rugăm să vă autentificați', en: 'Please log in' },

    // Messages
    'data-saved-success': { ro: 'Datele au fost salvate cu succes!', en: 'Data saved successfully!' },
    'error-loading-data': { ro: 'Eroare la încărcarea datelor', en: 'Error loading data' },
    'error-saving-data': { ro: 'Eroare la salvarea datelor', en: 'Error saving data' },
    'confirm-delete': { ro: 'Ești sigur că vrei să ștergi?', en: 'Are you sure you want to delete?' },
    'no-data-available': { ro: 'Nu sunt date disponibile', en: 'No data available' },
    'access-denied': { ro: 'Acces interzis', en: 'Access denied' },
    'login-required': { ro: 'Este necesară autentificarea', en: 'Login required' },

    // Time periods
    today: { ro: 'Astăzi', en: 'Today' },
    tomorrow: { ro: 'Mâine', en: 'Tomorrow' },
    yesterday: { ro: 'Ieri', en: 'Yesterday' },
    'this-week': { ro: 'Săptămâna aceasta', en: 'This week' },
    'next-week': { ro: 'Săptămâna viitoare', en: 'Next week' },
    'this-month': { ro: 'Luna aceasta', en: 'This month' },
    'next-month': { ro: 'Luna viitoare', en: 'Next month' },

    // Days of week
    monday: { ro: 'Luni', en: 'Monday' },
    tuesday: { ro: 'Marți', en: 'Tuesday' },
    wednesday: { ro: 'Miercuri', en: 'Wednesday' },
    thursday: { ro: 'Joi', en: 'Thursday' },
    friday: { ro: 'Vineri', en: 'Friday' },
    saturday: { ro: 'Sâmbătă', en: 'Saturday' },
    sunday: { ro: 'Duminică', en: 'Sunday' },

    // Months
    january: { ro: 'Ianuarie', en: 'January' },
    february: { ro: 'Februarie', en: 'February' },
    march: { ro: 'Martie', en: 'March' },
    april: { ro: 'Aprilie', en: 'April' },
    may: { ro: 'Mai', en: 'May' },
    june: { ro: 'Iunie', en: 'June' },
    july: { ro: 'Iulie', en: 'July' },
    august: { ro: 'August', en: 'August' },
    september: { ro: 'Septembrie', en: 'September' },
    october: { ro: 'Octombrie', en: 'October' },
    november: { ro: 'Noiembrie', en: 'November' },
    december: { ro: 'Decembrie', en: 'December' }
  };

  // Get translation for a key
  t(key, defaultValue = '') {
    const translation = this.translations[key];
    if (!translation) {
      console.warn(`Translation missing for key: ${key}`);
      return defaultValue || key;
    }
    return translation[this.currentLanguage] || translation['ro'] || defaultValue || key;
  }

  // Get all available languages
  getAvailableLanguages() {
    return [
      { code: 'ro', name: 'Română', flag: '🇷🇴' },
      { code: 'en', name: 'English', flag: '🇺🇸' }
    ];
  }

  // Format date according to current language
  formatDate(date, options = {}) {
    const locale = this.currentLanguage === 'ro' ? 'ro-RO' : 'en-US';
    return new Date(date).toLocaleDateString(locale, options);
  }

  // Format time according to current language
  formatTime(date, options = {}) {
    const locale = this.currentLanguage === 'ro' ? 'ro-RO' : 'en-US';
    return new Date(date).toLocaleTimeString(locale, options);
  }

  // Format currency (LEI for Romanian farms)
  formatCurrency(amount, currency = 'RON') {
    const locale = this.currentLanguage === 'ro' ? 'ro-RO' : 'en-US';
    
    // For RON, show as "123,45 LEI" instead of "123,45 RON"
    if (currency === 'RON') {
      return new Intl.NumberFormat(locale, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(amount) + ' LEI';
    }
    
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: currency
    }).format(amount);
  }

  // Format number according to current language
  formatNumber(number, options = {}) {
    const locale = this.currentLanguage === 'ro' ? 'ro-RO' : 'en-US';
    return new Intl.NumberFormat(locale, options).format(number);
  }
}

// Create singleton instance
const translationService = new TranslationService();

// React hook for using translations
export const useTranslation = () => {
  const [language, setLanguage] = useState(translationService.getCurrentLanguage());

  useEffect(() => {
    const unsubscribe = translationService.addLanguageListener(setLanguage);
    return unsubscribe;
  }, []);

  return {
    t: translationService.t.bind(translationService),
    language,
    setLanguage: translationService.setLanguage.bind(translationService),
    availableLanguages: translationService.getAvailableLanguages(),
    formatDate: translationService.formatDate.bind(translationService),
    formatTime: translationService.formatTime.bind(translationService),
    formatCurrency: translationService.formatCurrency.bind(translationService),
    formatNumber: translationService.formatNumber.bind(translationService)
  };
};

export default translationService; 
 